import React, { useEffect } from "react";
import { inject, observer } from "mobx-react";

import { GlobalProps } from "App";
import { withTheme } from "styled-components";
import { FullPageContainer } from "components/globals";
import { DesignationStore } from "stores/DesignationStore";
import { ShiftStore } from "stores/ShiftStore";
import { PermissionStore } from "stores/PermissionStore";
import { UserStore } from "stores/UserStore";
import { authService } from "services/AuthService";
import {
  STORE_DESIGNATION,
  STORE_PERMISSION,
  STORE_SHIFT,
  STORE_USER,
} from "constants/stores";
import Loader from "components/shared/loader/Loader";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { getCompleteRoute, RouteComponents } from "utils/routing";
import { FORCE_PASSWORD_CHANGE_EVENT } from "constants/constants";

interface SplashScreenProps extends GlobalProps {
  [STORE_DESIGNATION]?: DesignationStore;
  [STORE_SHIFT]?: ShiftStore;
  [STORE_PERMISSION]?: PermissionStore;
  [STORE_USER]?: UserStore;
  children?: React.ReactElement;
}

const SplashScreen: React.FC<SplashScreenProps> = (
  props: SplashScreenProps,
) => {
  const {
    designationStore,
    permissionStore,
    shiftStore,
    userStore,
    children,
  } = props;
  const { currentShiftItem } = shiftStore!;
  const history = useHistory();
  const accountRoute = getCompleteRoute(RouteComponents.ACCOUNT);

  useEffect(() => {
    const fetchPermission = async () => {
      if (
        permissionStore!.permissionList.length < 1 &&
        !permissionStore!.isLoading
      ) {
        return permissionStore!.fetchPermissions();
      }
      return Promise.resolve();
    };

    const checkTokenExpiry = async () => {
      if (authService.hasAuthToken) {
        try {
          await userStore!.getMe();
          if (userStore!.mustChangePasswordRequired) {
            history.push(accountRoute);
            return;
          }
          await Promise.all([
            designationStore!.fetchDesignations(),
            shiftStore!.fetchCurrentShift(),
            shiftStore!.fetchShiftType(),
            fetchPermission(),
          ]);
        } catch (e) {
          toast.error(e.message);
        }
      }
    };

    checkTokenExpiry();
  }, [designationStore, history, permissionStore, shiftStore, userStore]);

  useEffect(() => {
    const handleForcePasswordChange = () => {
      if (!userStore) {
        return;
      }
      if (!userStore.mustChangePasswordRequired) {
        userStore.setMustChangePasswordRequired(true);
      }
      history.push(accountRoute);
    };

    window.addEventListener(
      FORCE_PASSWORD_CHANGE_EVENT,
      handleForcePasswordChange,
    );

    return () => {
      window.removeEventListener(
        FORCE_PASSWORD_CHANGE_EVENT,
        handleForcePasswordChange,
      );
    };
  }, [history, userStore]);

  const loading = () =>
    userStore!.loggedInUserItem.loading ||
    designationStore!.designations.loading ||
    permissionStore!.isLoading ||
    currentShiftItem.loading ||
    shiftStore!.isShiftTypeListFetching;

  if (loading()) {
    return (
      <FullPageContainer>
        <Loader />
      </FullPageContainer>
    );
  }
  return <>{children}</>;
};

export default inject(
  STORE_DESIGNATION,
  STORE_SHIFT,
  STORE_PERMISSION,
  STORE_USER,
)(withTheme(observer(SplashScreen)));
